// 保存・シリアライズの wire 型（§8）。計算ドメイン型は core から再利用する。
import type { RateMode } from '../core/types';

export type { RateMode } from '../core/types';

/** localStorage / JSON のスキーマ版。破壊的変更時にインクリメントし io.ts で移行。 */
export const SCHEMA_VERSION = 2;

export type CurrencyId = string;

/**
 * cost・drop 共通の行（§4.2）。「2概念の分離」:
 * - `itemId` あり = カタログの価格付きアイテム（自動相場）。価値 = rate(itemId)、`unit` は表示用オーブ。
 * - `itemId` なし = 手動アイテム。価値 = `value` × rate(`unit`)。`name` は自由入力。
 */
export interface ValueRow {
  id: string;
  /** 表示名（カタログ品はその Text、手動は自由入力）。 */
  name: string;
  /** カタログアイテム id（自動相場）。無ければ手動。 */
  itemId?: string;
  /** 価値のオーブ単位（div/chaos/exalted）。 */
  unit: CurrencyId;
  /** 1個あたりの価値（`unit` 建て）。手動は入力値、自動は表示時に rate から再算出。 */
  value: number;
  /** cost=消費個数 q / drop=λ 表示値。 */
  count: number;
}

export interface CostLine extends ValueRow {
  role: 'cost';
}

export interface DropLine extends ValueRow {
  role: 'drop';
  /** λ 推定モード（§5.2）。direct の lambda は count と同期。 */
  rateMode: RateMode;
}

/** グループドロップのメンバー（§5.2.2）。 */
export interface GroupMember {
  id: string;
  name: string;
  itemId?: string;
  unit: CurrencyId;
  value: number;
  /** グループ内の相対重み（比）。確定 m 個を λ_i = m·p_i/Σp で正規化配分（§5.2.2）。 */
  p: number;
  /** 任意: p_i 自体を実績推定する場合の推定モード。 */
  rateMode?: RateMode;
}

/** 「グループから確定 m 個」型のドロップ（§5.2.2）。計算時に λ_i=m·p_i の独立行へ展開。 */
export interface GroupDrop {
  kind: 'group';
  id: string;
  name: string;
  m: number;
  members: GroupMember[];
}

export type DropEntry = DropLine | GroupDrop;

/** 名前付きファーム設定（§8）。リーグごとに保持。 */
export interface FarmSet {
  schemaVersion: number;
  id: string;
  name: string;
  /** 所属リーグ（§8 の所属キー）。 */
  league: string;
  displayUnit: CurrencyId;
  /** 自由記入のメモ（マップ・タブレット・装備構成などの覚書）。計算には使わない。 */
  notes?: string;
  /** 1周の所要時間 t（秒）。 */
  time: number;
  steady: { amount: number; unit: CurrencyId };
  costLines: CostLine[];
  dropLines: DropEntry[];
  /** 現在の軍資金（base 建てで保持・表示時に換算, §5.1/§5.6）。判定表示のみ。 */
  currentBankroll?: number;
  updatedAt: number;
}

export function isGroupDrop(entry: DropEntry): entry is GroupDrop {
  return (entry as GroupDrop).kind === 'group';
}
